import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FeatureToggle } from '../feature-toggle.model';
import { FeatureToggleService } from '../feature-toggle.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { NotificationService } from '@app/shared/notification/notification.service';

interface ToggleRow {
  toggle: FeatureToggle;
  control: FormControl<boolean>;
  saving: boolean;
}

@Component({
  selector: 'app-feature-toggles-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CheckboxComponent],
  templateUrl: './feature-toggles-page.component.html',
})
export class FeatureTogglesPageComponent implements OnInit {
  rows: ToggleRow[] = [];
  loading = false;

  constructor(
    private readonly service: FeatureToggleService,
    private readonly notifications: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service.list().subscribe({
      next: (toggles) => {
        this.rows = toggles.map((toggle) => this.buildRow(toggle));
        this.loading = false;
      },
      error: () => {
        this.notifications.error('Failed to load feature toggles');
        this.loading = false;
      },
    });
  }

  formatTimestamp(value: string | null): string {
    if (!value) {
      return 'Never';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${day} ${month} ${year}, ${time}`;
  }

  private buildRow(toggle: FeatureToggle): ToggleRow {
    const control = new FormControl<boolean>(toggle.enabled, { nonNullable: true });

    const row: ToggleRow = { toggle, control, saving: false };

    control.valueChanges.subscribe((value) => this.persist(row, value));

    return row;
  }

  private persist(row: ToggleRow, value: boolean): void {
    if (value === row.toggle.enabled) {
      return;
    }
    row.saving = true;

    this.service.update(row.toggle.key, value).subscribe({
      next: (updated) => {
        row.toggle = updated;
        row.saving = false;
        this.notifications.success(
          `${updated.label} ${updated.enabled ? 'enabled' : 'disabled'}`,
        );
      },
      error: (err) => {
        row.saving = false;
        // Revert the optimistic switch.
        row.control.setValue(row.toggle.enabled, { emitEvent: false });
        this.notifications.error(
          err?.error?.message || 'Failed to update feature toggle',
        );
      },
    });
  }
}
